import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Divider, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import CheckoutBlock from './CheckoutBlock/CheckoutBlock'
import useReturnProtectionAppearance from './hooks/useReturnProtectionAppearance/useReturnProtectionAppearance'
import PopupBlock from './PopupBlock/PopupBlock'
import PreviewBlock from './PreviewBlock/PreviewBlock'
import WidgetAndPopupTabSkeleton from './WidgetAndPopupTabSkeleton/WidgetAndPopupTabSkeleton'
import WidgetBlock from './WidgetBlock/WidgetBlock'
import * as Styles from './styles'

const WidgetAndPopupTab = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()

  const canEdit = hasPermission(userPermissions.editReturnProtection)

  const {
    appearance,
    appearanceError,
    fieldErrors,
    isBusy,
    savingAppearance,
    resettingWidget,
    resettingPopup,
    hasChanges,
    hasNewHeaderImage,
    uploaderKey,
    changeField,
    changeHeaderImage,
    discardHeaderImage,
    revertChanges,
    onSave,
    onResetWidget,
    onResetPopup,
  } = useReturnProtectionAppearance({ storeId: info.selectedStore.id, setToastInfo })

  const fieldsDisabled = !canEdit || savingAppearance

  if (appearanceError) {
    return <Alert type="error">{t('No se ha podido cargar la apariencia del widget y el popup.')}</Alert>
  }

  if (!appearance) {
    return <WidgetAndPopupTabSkeleton />
  }

  return (
    <Styles.Container>
      {hasChanges && <UnsavedChangesAlert hasPermission={canEdit} />}
      <WidgetBlock
        appearance={appearance}
        fieldErrors={fieldErrors}
        onChangeColor={changeField}
        onReset={onResetWidget}
        resetting={resettingWidget}
        disabled={fieldsDisabled}
        resetDisabled={!canEdit || isBusy}
      />
      <Divider />
      <CheckoutBlock appearance={appearance} onChange={changeField} disabled={fieldsDisabled} />
      <Divider />
      <PopupBlock
        appearance={appearance}
        fieldErrors={fieldErrors}
        uploaderKey={uploaderKey}
        hasNewHeaderImage={hasNewHeaderImage}
        onChangeColor={changeField}
        onChangeHeaderImage={changeHeaderImage}
        onDiscardHeaderImage={discardHeaderImage}
        onReset={onResetPopup}
        resetting={resettingPopup}
        disabled={fieldsDisabled}
        resetDisabled={!canEdit || isBusy}
      />
      <Divider />
      <PreviewBlock appearance={appearance} />
      <SaveChangesButtons
        hasChanges={canEdit && hasChanges}
        onSave={onSave}
        onRevertChanges={revertChanges}
        loading={savingAppearance}
        disabled={isBusy}
      />
    </Styles.Container>
  )
}

export default WidgetAndPopupTab
